/**
 * outreachEngine.js — Cadence logic, XP computation, streak tracking
 *
 * All functions are pure — derive state from outreach_actions data.
 * No separate user_xp or cadence table needed.
 */

// ── Cadence Plan (from playbook) ────────────────────────────
// Legacy single cadence — kept as fallback for unclassified
export const CADENCE_STEPS = [
  { day: 1,  label: 'Initial Outreach',   channel: 'email',     actionType: 'initial',   xp: 10 },
  { day: 3,  label: 'Social Engagement',  channel: 'social',    actionType: 'social',    xp: 3  },
  { day: 5,  label: 'Follow-Up #1',       channel: 'text',      actionType: 'follow_up', xp: 8  },
  { day: 10, label: 'Portfolio Share',     channel: 'email',     actionType: 'follow_up', xp: 8  },
  { day: 15, label: 'Follow-Up #2',       channel: 'phone',     actionType: 'phone',     xp: 12 },
  { day: 25, label: 'Free Shoot Offer',   channel: 'email',     actionType: 'follow_up', xp: 10 },
  { day: 45, label: 'Re-Engage',          channel: 'email',     actionType: 'follow_up', xp: 8  },
];

// ── Segment-Specific Cadences ───────────────────────────────
export const CADENCE_BY_SEGMENT = {
  top_producer: [
    { day: 1,  label: 'Intro — Production Company',  channel: 'email',        actionType: 'initial',   xp: 10 },
    { day: 3,  label: 'Engage Their Content',         channel: 'social',       actionType: 'social',    xp: 3  },
    { day: 7,  label: 'Case Study / Portfolio',       channel: 'email',        actionType: 'follow_up', xp: 8  },
    { day: 14, label: 'Follow-Up Text',               channel: 'text',         actionType: 'follow_up', xp: 8  },
    { day: 21, label: 'Phone Call',                    channel: 'phone',        actionType: 'phone',     xp: 12 },
    { day: 45, label: 'Direct Ask',                    channel: 'email',        actionType: 'follow_up', xp: 10 },
  ],
  luxury: [
    { day: 1,  label: 'Intro — Luxury Film + FPV',    channel: 'email',        actionType: 'initial',   xp: 10 },
    { day: 3,  label: 'Engage + DM',                  channel: 'instagram_dm', actionType: 'social',    xp: 3  },
    { day: 7,  label: 'FPV Sample Link',              channel: 'text',         actionType: 'follow_up', xp: 8  },
    { day: 14, label: 'Portfolio Email',               channel: 'email',        actionType: 'follow_up', xp: 8  },
    { day: 21, label: 'Phone Call',                    channel: 'phone',        actionType: 'phone',     xp: 12 },
    { day: 30, label: 'Free Reel at Open House',      channel: 'email',        actionType: 'follow_up', xp: 10 },
    { day: 45, label: 'Follow-Up',                    channel: 'email',        actionType: 'follow_up', xp: 8  },
  ],
  mid_volume: [
    { day: 1,  label: 'Intro — Bundle Value',          channel: 'email',        actionType: 'initial',   xp: 10 },
    { day: 3,  label: 'Engage + DM',                   channel: 'instagram_dm', actionType: 'social',    xp: 3  },
    { day: 7,  label: 'Follow-Up Text',                channel: 'text',         actionType: 'follow_up', xp: 8  },
    { day: 14, label: 'Bundle Reminder',               channel: 'email',        actionType: 'follow_up', xp: 8  },
    { day: 21, label: 'Phone Call',                     channel: 'phone',        actionType: 'phone',     xp: 12 },
    { day: 30, label: 'Free Reel Offer',               channel: 'email',        actionType: 'follow_up', xp: 10 },
  ],
  new_agent: [
    { day: 1,  label: 'Intro — First Listing Special', channel: 'email',        actionType: 'initial',   xp: 10 },
    { day: 3,  label: 'Engage + DM',                   channel: 'instagram_dm', actionType: 'social',    xp: 3  },
    { day: 7,  label: 'Portfolio Link',                 channel: 'text',         actionType: 'follow_up', xp: 8  },
    { day: 14, label: 'Quick Call',                     channel: 'phone',        actionType: 'phone',     xp: 12 },
    { day: 21, label: 'Standing Offer',                 channel: 'email',        actionType: 'follow_up', xp: 8  },
  ],
};

// Unclassified uses mid_volume cadence
CADENCE_BY_SEGMENT.unknown = CADENCE_BY_SEGMENT.mid_volume;

/** Get cadence steps for a given segment */
export function getCadenceForSegment(segment) {
  return CADENCE_BY_SEGMENT[segment] || CADENCE_BY_SEGMENT.unknown;
}

// ── XP Table ────────────────────────────────────────────────
export const XP_TABLE = {
  research:  5,
  initial:   10,
  follow_up: 8,
  social:    3,
  phone:     12,
  response:  25,
  booked:    50,
  converted: 100,
};

// ── Levels ──────────────────────────────────────────────────
export const LEVELS = [
  { level: 1, xp: 0,    title: 'Spotter' },
  { level: 2, xp: 100,  title: 'Scout' },
  { level: 3, xp: 300,  title: 'Wingman' },
  { level: 4, xp: 600,  title: 'Navigator' },
  { level: 5, xp: 1000, title: 'Ace' },
  { level: 6, xp: 1500, title: 'Top Gun' },
  { level: 7, xp: 2500, title: 'Maverick' },
];

// ── Outreach Statuses ───────────────────────────────────────
export const OUTREACH_STATUSES = [
  'new', 'active', 'researching', 'contacted', 'follow_up', 'responded', 'booked', 'client', 'declined',
];

export const OUTREACH_STATUS_COLORS = {
  new:         '#94A3B8',
  active:      '#60A5FA',
  researching: '#818CF8',
  contacted:   '#38BDF8',
  follow_up:   '#FB923C',
  responded:   '#4ADE80',
  booked:      '#22D3EE',
  client:      '#A3E635',
  declined:    '#F87171',
};

export const SEGMENTS = ['top_producer', 'luxury', 'mid_volume', 'new_agent', 'unknown'];

export const SEGMENT_LABELS = {
  top_producer: 'Top Producer',
  luxury:       'Luxury',
  mid_volume:   'Mid-Volume',
  new_agent:    'New Agent',
  unknown:      'Unclassified',
};

// ── Agent Classification Scoring ────────────────────────────
// Two-dimensional: Activity Level (Elite/Active/Emerging/Inactive) × Market Type (Luxury/Standard/Mixed)
// Maps to 4 internal segments for cadence/templates

export const ACTIVITY_LEVELS = ['elite', 'active', 'emerging', 'inactive', 'unclassified'];
export const MARKET_TYPES = ['luxury', 'mixed', 'standard'];

export const ACTIVITY_LABELS = {
  elite: 'Elite', active: 'Active', emerging: 'Emerging', inactive: 'Inactive', unclassified: 'Unclassified',
};
export const MARKET_LABELS = {
  luxury: 'Luxury', mixed: 'Mixed', standard: 'Standard',
};

/**
 * Score an agent based on available metrics.
 * Missing fields are excluded — score is normalized to what's available.
 *
 * @param {Object} metrics — from AI research or manual input
 * @param {number|null} metrics.listings_12m — listings last 12 months
 * @param {number|null} metrics.avg_price — average listing price in dollars
 * @param {number|null} metrics.luxury_pct — % of listings >= $2M (0-100)
 * @param {number|null} metrics.years_active — years in business
 * @param {number|null} metrics.social_score — 0-10, manual or AI-estimated
 * @param {number|null} metrics.brand_score — 0-10, manual or AI-estimated
 * @returns {{ totalScore, maxPossible, normalizedScore, breakdown, activityLevel, marketType, classification, segment, missingFields }}
 */
export function classifyAgent(metrics = {}) {
  const breakdown = {};
  let total = 0;
  let maxPossible = 0;
  const missing = [];

  // 1. Listings score (max 25)
  if (metrics.listings_12m != null) {
    const l = metrics.listings_12m;
    const s = l === 0 ? 0 : l < 5 ? 8 : l < 10 ? 15 : l < 20 ? 20 : 25;
    breakdown.listings = s;
    total += s;
    maxPossible += 25;
  } else { missing.push('listings_12m'); }

  // 2. Price score (max 25)
  if (metrics.avg_price != null) {
    const p = metrics.avg_price;
    const s = p < 1_000_000 ? 5 : p < 2_000_000 ? 12 : p < 4_000_000 ? 20 : 25;
    breakdown.price = s;
    total += s;
    maxPossible += 25;
  } else { missing.push('avg_price'); }

  // 3. Luxury % score (max 25)
  if (metrics.luxury_pct != null) {
    const lp = metrics.luxury_pct;
    const s = lp < 20 ? 5 : lp < 50 ? 12 : lp < 80 ? 18 : 25;
    breakdown.luxury = s;
    total += s;
    maxPossible += 25;
  } else { missing.push('luxury_pct'); }

  // 4. Experience score (max 15)
  if (metrics.years_active != null) {
    const y = metrics.years_active;
    const s = y < 2 ? 3 : y < 5 ? 8 : y < 10 ? 12 : 15;
    breakdown.experience = s;
    total += s;
    maxPossible += 15;
  } else { missing.push('years_active'); }

  // 5. Brand score (max 10) — only if at least one of social/brand provided
  if (metrics.social_score != null || metrics.brand_score != null) {
    const social = metrics.social_score ?? 0;
    const brand = metrics.brand_score ?? 0;
    const count = (metrics.social_score != null ? 1 : 0) + (metrics.brand_score != null ? 1 : 0);
    const avg = (social + brand) / count;
    breakdown.brand = Math.round(avg);
    total += breakdown.brand;
    maxPossible += 10;
  } else {
    missing.push('social_score');
    missing.push('brand_score');
  }

  // Normalize to 0-100 scale based on available fields
  const normalizedScore = maxPossible > 0 ? Math.round((total / maxPossible) * 100) : 0;

  // Activity level
  let activityLevel;
  if (maxPossible === 0) activityLevel = 'unclassified';
  else if (metrics.listings_12m === 0) activityLevel = 'inactive';
  else if (normalizedScore >= 80) activityLevel = 'elite';
  else if (normalizedScore >= 60) activityLevel = 'active';
  else if (normalizedScore >= 35) activityLevel = 'emerging';
  else activityLevel = 'inactive';

  // Market type — based on luxury_pct if available, else price
  let marketType = 'standard';
  if (metrics.luxury_pct != null) {
    marketType = metrics.luxury_pct >= 70 ? 'luxury' : metrics.luxury_pct >= 30 ? 'mixed' : 'standard';
  } else if (metrics.avg_price != null) {
    marketType = metrics.avg_price >= 3_000_000 ? 'luxury' : metrics.avg_price >= 1_500_000 ? 'mixed' : 'standard';
  }

  const classification = activityLevel === 'unclassified'
    ? 'Unclassified'
    : `${ACTIVITY_LABELS[activityLevel]} – ${MARKET_LABELS[marketType]}`;

  // Map to internal 4-segment for cadence/templates
  let segment;
  if (activityLevel === 'elite') segment = 'top_producer';
  else if (marketType === 'luxury' && activityLevel !== 'inactive') segment = 'luxury';
  else if (metrics.years_active != null && metrics.years_active < 2 && activityLevel !== 'elite') segment = 'new_agent';
  else if (activityLevel === 'inactive') segment = 'new_agent';
  else segment = 'mid_volume';

  return {
    totalScore: total,
    maxPossible,
    normalizedScore,
    breakdown,
    activityLevel,
    marketType,
    classification,
    segment,
    missingFields: missing,
  };
}

export const CHANNELS = ['email', 'text', 'phone', 'instagram_dm', 'social', 'in_person'];

export const CHANNEL_LABELS = {
  email:         'Email',
  text:          'Text',
  phone:         'Phone',
  instagram_dm:  'Instagram DM',
  social:        'Social',
  in_person:     'In Person',
};

// ── Pure Helpers ────────────────────────────────────────────

/** Get XP for a given action type */
export function getXP(actionType) {
  return XP_TABLE[actionType] || 0;
}

/** Calculate total XP from an array of outreach actions */
export function calculateTotalXP(actions) {
  return actions.reduce((sum, a) => sum + getXP(a.actionType), 0);
}

/** Get level info for a given XP total */
export function getLevelForXP(totalXP) {
  let result = LEVELS[0];
  for (const lvl of LEVELS) {
    if (totalXP >= lvl.xp) result = lvl;
    else break;
  }
  const nextLevel = LEVELS.find(l => l.level === result.level + 1);
  return {
    ...result,
    totalXP,
    nextLevelXP: nextLevel?.xp || null,
    progress: nextLevel ? (totalXP - result.xp) / (nextLevel.xp - result.xp) : 1,
  };
}

/**
 * Compute cadence state for a prospect.
 * @param {Array} actions — outreach_actions for this contact, sorted by created_at ASC
 * @param {string} [segment] — prospect segment for cadence lookup
 * @returns {{ currentStep, nextStep, startedAt, completedSteps, nextActionDate, isComplete, cadenceSteps }}
 */
export function getCadenceState(actions, segment) {
  const steps = segment ? getCadenceForSegment(segment) : CADENCE_STEPS;

  if (!actions.length) {
    return { currentStep: 0, nextStep: steps[0], startedAt: null, completedSteps: [], nextActionDate: null, isComplete: false, cadenceSteps: steps };
  }

  // Find the initial outreach (first action with cadence_day = 1 or actionType = 'initial')
  const initialAction = actions.find(a => a.cadenceDay === 1 || a.actionType === 'initial');
  const startedAt = initialAction?.createdAt || actions[0].createdAt;

  // Which cadence days have been completed?
  const completedDays = new Set(actions.filter(a => a.cadenceDay).map(a => a.cadenceDay));

  const completedSteps = steps.filter(s => completedDays.has(s.day));
  const nextStep = steps.find(s => !completedDays.has(s.day));

  // Calculate next action date
  let nextActionDate = null;
  if (nextStep && startedAt) {
    const start = new Date(startedAt);
    nextActionDate = new Date(start);
    nextActionDate.setDate(start.getDate() + nextStep.day - 1);
  }

  return {
    currentStep: completedSteps.length,
    nextStep: nextStep || null,
    startedAt,
    completedSteps,
    nextActionDate: nextActionDate ? `${nextActionDate.getFullYear()}-${String(nextActionDate.getMonth() + 1).padStart(2, '0')}-${String(nextActionDate.getDate()).padStart(2, '0')}` : null,
    isComplete: !nextStep,
    cadenceSteps: steps,
  };
}

/**
 * Compute daily streak (weekdays only).
 * @param {Array} actions — all actions for a user, sorted by created_at DESC
 * @returns {{ streak, multiplier, bestStreak }}
 */
export function computeStreak(actions) {
  if (!actions.length) return { streak: 0, multiplier: 1.0, bestStreak: 0 };

  // Group actions by YYYY-MM-DD
  const daySet = new Set();
  for (const a of actions) {
    const d = new Date(a.createdAt);
    daySet.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let streak = 0;
  let current = new Date(today);

  // Walk backwards from today, counting consecutive weekdays with activity
  for (let i = 0; i < 365; i++) {
    const dayOfWeek = current.getDay();
    const dateStr = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}-${String(current.getDate()).padStart(2, '0')}`;

    // Skip weekends
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      current.setDate(current.getDate() - 1);
      continue;
    }

    if (daySet.has(dateStr)) {
      streak++;
      current.setDate(current.getDate() - 1);
    } else if (i === 0) {
      // Today has no activity yet — don't break streak, just don't count today
      current.setDate(current.getDate() - 1);
    } else {
      break;
    }
  }

  // Multiplier
  let multiplier = 1.0;
  if (streak >= 30) multiplier = 1.5;
  else if (streak >= 7) multiplier = 1.2;
  else if (streak >= 3) multiplier = 1.1;

  return { streak, multiplier, bestStreak: streak };
}

/**
 * Get all "missions" due today across all active prospects.
 * Includes cadence missions AND follow-up reminders.
 * @param {Array} prospects — contacts with type='prospect'
 * @param {Map} actionsByContact — Map<contactId, actions[]>
 * @returns {Array} — sorted by urgency (overdue first, then today, then upcoming)
 */
export function getTodaysMissions(prospects, actionsByContact) {
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const missions = [];

  for (const prospect of prospects) {
    const status = prospect.data?.outreach_status || prospect.data?.outreachStatus || 'new';
    // Skip terminal statuses and unactivated prospects
    if (status === 'declined' || status === 'client' || status === 'new') continue;

    // Skip snoozed prospects
    const snoozedUntil = prospect.data?.snoozed_until;
    if (snoozedUntil && snoozedUntil > today) continue;

    const actions = actionsByContact.get(prospect.id) || [];
    const segment = prospect.data?.segment || 'unknown';

    // Follow-up reminders (from response tracking)
    const followUpDate = prospect.data?.follow_up_date;
    if (followUpDate && followUpDate <= today) {
      const followUpNote = prospect.data?.follow_up_note || 'Follow up on response';
      missions.push({
        prospect,
        step: { day: 0, label: followUpNote, channel: 'phone', actionType: 'follow_up', xp: 8 },
        dueDate: followUpDate,
        urgency: followUpDate < today ? 'overdue' : 'today',
        isFollowUp: true,
      });
      continue; // Don't also show cadence mission — follow-up takes priority
    }

    // Cadence missions
    const cadence = getCadenceState(actions, segment);

    if (cadence.isComplete || !cadence.nextStep) continue;
    if (!cadence.nextActionDate) {
      // Activated but never contacted — Day 1 due today
      missions.push({ prospect, step: cadence.nextStep, dueDate: today, urgency: 'new', isFollowUp: false });
      continue;
    }

    const due = cadence.nextActionDate;
    if (due <= today) {
      missions.push({ prospect, step: cadence.nextStep, dueDate: due, urgency: due < today ? 'overdue' : 'today', isFollowUp: false });
    }
  }

  // Sort: overdue first, then today, then new
  const urgencyOrder = { overdue: 0, today: 1, new: 2 };
  missions.sort((a, b) => (urgencyOrder[a.urgency] || 9) - (urgencyOrder[b.urgency] || 9));

  return missions;
}

/**
 * Group unstarted prospects (status='new') by brokerage, with team sub-groups.
 * @param {Array} prospects — all prospects
 * @param {Map} actionsByContact — Map<contactId, actions[]>
 * @returns {Array} — [{ brokerage, agents[], count, teams: [{ team, agents[] }] }] sorted by count desc
 */
export function getUnstartedByBrokerage(prospects, actionsByContact) {
  const groups = new Map();

  for (const p of prospects) {
    const status = p.data?.outreach_status || p.data?.outreachStatus || 'new';
    if (status !== 'new') continue;
    const actions = actionsByContact.get(p.id) || [];
    if (actions.length > 0) continue;

    const brokerage = p.company || 'Independent';
    if (!groups.has(brokerage)) groups.set(brokerage, []);
    groups.get(brokerage).push(p);
  }

  return [...groups.entries()]
    .map(([brokerage, agents]) => {
      // Build team sub-groups
      const teamMap = new Map();
      for (const a of agents) {
        const team = a.data?.team || '';
        if (team) {
          if (!teamMap.has(team)) teamMap.set(team, []);
          teamMap.get(team).push(a);
        }
      }
      const teams = [...teamMap.entries()]
        .map(([team, members]) => ({ team, agents: members, count: members.length }))
        .sort((a, b) => b.count - a.count);

      return { brokerage, agents, count: agents.length, teams };
    })
    .sort((a, b) => b.count - a.count);
}

// Status progression order (index = rank, higher = further along)
const STATUS_RANK = { new: 0, active: 1, researching: 2, contacted: 3, follow_up: 4, responded: 5, booked: 6, client: 7, declined: -1 };

/**
 * Get the next auto-status based on the action type logged.
 * Status only advances forward, never backward.
 * @param {string} currentStatus
 * @param {string} actionType
 * @returns {string|null} — new status, or null if no change
 */
export function getNextStatus(currentStatus, actionType) {
  let target = null;
  if (['initial', 'follow_up', 'social', 'phone'].includes(actionType)) {
    target = 'contacted';
  } else if (actionType === 'response') {
    target = 'responded';
  } else if (actionType === 'booked') {
    target = 'booked';
  } else if (actionType === 'converted') {
    target = 'client';
  }
  if (!target) return null;
  const currentRank = STATUS_RANK[currentStatus] ?? 0;
  const targetRank = STATUS_RANK[target] ?? 0;
  return targetRank > currentRank ? target : null;
}

/**
 * Per-user stats from actions.
 */
export function getUserStats(actions, userId) {
  const userActions = userId ? actions.filter(a => a.userId === userId) : actions;
  const totalXP = calculateTotalXP(userActions);
  const level = getLevelForXP(totalXP);
  const streak = computeStreak(userActions);
  const totalActions = userActions.length;
  const responses = userActions.filter(a => a.actionType === 'response').length;
  const bookings = userActions.filter(a => a.actionType === 'booked').length;
  const responseRate = totalActions > 0 ? responses / totalActions : 0;

  return { totalXP, level, streak, totalActions, responses, bookings, responseRate };
}

// ── Experience (DRE license) ────────────────────────────────
const addYears = (d, n) => { const r = new Date(d); r.setFullYear(d.getFullYear() + n); return r; };

/**
 * Years licensed, from the DRE keys in contacts.data. Unknown → null
 * (no fallback to ai_research.years_active — it proved unreliable).
 * @returns {null | { years, startYear, issued, exactDate, lowerBound, licenseId, broker, moved }}
 */
export function getExperience(data, now = new Date()) {
  if (!data) return null;
  let issued = null;
  let exactDate = false;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(data.license_issued || '');
  if (m) {
    issued = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    exactDate = true;
  } else {
    const y = Number(data.experience_start_year);
    if (Number.isInteger(y) && y > 1900) issued = new Date(y, 0, 1);
  }
  if (!issued || isNaN(issued)) return null;
  let whole = now.getFullYear() - issued.getFullYear();
  if (addYears(issued, whole) > now) whole -= 1;
  const anniv = addYears(issued, whole);
  return {
    years: Math.max(0, whole + (now - anniv) / (addYears(issued, whole + 1) - anniv)),
    startYear: issued.getFullYear(),
    issued,
    exactDate,
    lowerBound: data.experience_lower_bound === true,
    licenseId: data.dre_license_id || null,
    broker: data.dre_responsible_broker || null,
    moved: data.dre_broker_moved === true,
  };
}

/** "<1 yr" | "1 yr" | "12 yrs" ("≥" when lowerBound); short → "<1" | "12" | "≥46"; unknown → "—" */
export function formatExperience(exp, short = false) {
  if (!exp) return '—';
  const whole = Math.floor(exp.years);
  if (whole < 1) return short ? '<1' : '<1 yr';
  const n = `${exp.lowerBound ? '≥' : ''}${whole}`;
  return short ? n : `${n} ${whole === 1 ? 'yr' : 'yrs'}`;
}

// ── Outreach Natures ────────────────────────────────────────
// Nature = the intent/purpose of the message — determines prompt strategy
export const NATURES = [
  { key: 'introduction', label: 'Introduction',  channels: ['email', 'text', 'instagram_dm'] },
  { key: 'follow_up',    label: 'Follow-Up',     channels: ['email', 'text', 'instagram_dm'] },
  { key: 'value_drop',   label: 'Value Drop',    channels: ['email', 'text', 'instagram_dm'] },
  { key: 'free_offer',   label: 'Free Offer',    channels: ['email', 'text'] },
  { key: 're_engage',    label: 'Re-Engage',     channels: ['email', 'text', 'instagram_dm'] },
  { key: 'casual_touch', label: 'Casual Touch',  channels: ['text', 'instagram_dm'] },
];

export const NATURE_LABELS = Object.fromEntries(NATURES.map(n => [n.key, n.label]));

// Channels that get AI templates vs. notes-only
export const TEMPLATE_CHANNELS = ['email', 'text', 'instagram_dm'];
export const NOTE_CHANNELS = ['phone', 'in_person'];

/** Get available natures for a given channel */
export function getNaturesForChannel(channel) {
  return NATURES.filter(n => n.channels.includes(channel));
}

/** Infer default nature from a cadence step */
export function getNatureFromStep(step) {
  if (!step) return 'introduction';
  const { day, channel, actionType, label } = step;
  const lbl = (label || '').toLowerCase();

  // Phone/in-person are notes-only — no nature needed
  if (NOTE_CHANNELS.includes(channel)) return null;

  // Explicit signals from label
  if (lbl.includes('free') || lbl.includes('offer') || lbl.includes('reel at')) return 'free_offer';
  if (lbl.includes('engage') || lbl.includes('dm')) return 'casual_touch';
  if (lbl.includes('portfolio') || lbl.includes('case study') || lbl.includes('fpv sample') || lbl.includes('portfolio link')) return 'value_drop';
  if (lbl.includes('re-engage') || lbl.includes('re engage')) return 're_engage';

  // Day-based inference
  if (actionType === 'initial' || day === 1) return 'introduction';
  if (day >= 40) return 're_engage';
  if (day >= 20 && day <= 35) return 'free_offer';
  if (actionType === 'social') return 'casual_touch';

  return 'follow_up';
}
